import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import {
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import {
  paymentNotifyService,
  paymentOrderService,
  type PaymentNotify,
  type PaymentOrder,
  type PaymentOrderStats,
} from '../../services/paymentService';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface SearchFormValues {
  orderNo?: string;
  subject?: string;
  userName?: string;
  tradeNo?: string;
  channel?: string;
  payType?: string;
  status?: string;
  notifyStatus?: string;
  createdAt?: [Dayjs, Dayjs];
}

const channelMap: Record<string, { color: string; text: string }> = {
  wechat: { color: 'green', text: '微信支付' },
  alipay: { color: 'blue', text: '支付宝' },
  wallet: { color: 'orange', text: '钱包支付' },
};

const payTypeMap: Record<string, { color: string; text: string }> = {
  jsapi: { color: 'green', text: 'JSAPI' },
  native: { color: 'blue', text: 'Native' },
  app: { color: 'purple', text: 'App' },
  wap: { color: 'cyan', text: 'WAP' },
};

const paySceneMap: Record<string, { color: string; text: string }> = {
  mini_program: { color: 'green', text: '小程序' },
  app: { color: 'blue', text: 'App' },
  h5: { color: 'cyan', text: 'H5' },
  pc: { color: 'geekblue', text: 'PC' },
};

const bizTypeMap: Record<string, { color: string; text: string }> = {
  service_booking: { color: 'processing', text: '服务下单' },
};

const orderStatusMap: Record<string, { color: string; text: string }> = {
  pending: { color: 'warning', text: '待支付' },
  paid: { color: 'success', text: '已支付' },
  failed: { color: 'error', text: '支付失败' },
  closed: { color: 'default', text: '已关闭' },
  refunded: { color: 'purple', text: '已退款' },
};

const notifyStatusMap: Record<string, { color: string; text: string }> = {
  pending: { color: 'warning', text: '待回调' },
  success: { color: 'success', text: '回调成功' },
  failed: { color: 'error', text: '回调失败' },
  ignored: { color: 'default', text: '已忽略' },
  retrying: { color: 'processing', text: '重试中' },
};

const notifyTypeMap: Record<string, { color: string; text: string }> = {
  wechat_pay_notify: { color: 'green', text: '微信支付回调' },
  'TRANSACTION.SUCCESS': { color: 'green', text: '支付成功通知' },
  pay: { color: 'green', text: '支付通知' },
  refund: { color: 'orange', text: '退款通知' },
};

const defaultStats: PaymentOrderStats = {
  totalCount: 0,
  totalAmount: 0,
  paidCount: 0,
  paidAmount: 0,
  pendingCount: 0,
  notifyFailedCount: 0,
};

const renderTag = (
  value: string | undefined,
  mapper: Record<string, { color: string; text: string }>,
) => {
  const current = value ? mapper[value] : undefined;
  if (!value) {
    return <Tag>-</Tag>;
  }
  if (!current) {
    return <Tag>{value}</Tag>;
  }
  return <Tag color={current.color}>{current.text}</Tag>;
};

const formatMoney = (value?: number) => `¥${Number(value || 0).toFixed(2)}`;

const formatDateTime = (value?: string | null) => value || '-';

const formatPayload = (value?: string) => {
  if (!value) {
    return '-';
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const PaymentNotifyPage: React.FC = () => {
  const tableWrapRef = useHorizontalTableWheel();
  const [searchForm] = Form.useForm<SearchFormValues>();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<PaymentOrder[]>([]);
  const [stats, setStats] = useState<PaymentOrderStats>(defaultStats);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<PaymentOrder | null>(null);

  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyRows, setNotifyRows] = useState<PaymentNotify[]>([]);

  const [notifyDetailVisible, setNotifyDetailVisible] = useState(false);
  const [currentNotify, setCurrentNotify] = useState<PaymentNotify | null>(null);

  const buildQueryParams = useCallback((page: number, pageSize: number, values?: SearchFormValues) => {
    const formValues = values || searchForm.getFieldsValue();
    const params: Record<string, string | number> = {
      page,
      pageSize,
    };

    if (formValues.orderNo?.trim()) {
      params.orderNo = formValues.orderNo.trim();
    }
    if (formValues.subject?.trim()) {
      params.subject = formValues.subject.trim();
    }
    if (formValues.userName?.trim()) {
      params.userName = formValues.userName.trim();
    }
    if (formValues.tradeNo?.trim()) {
      params.tradeNo = formValues.tradeNo.trim();
    }
    if (formValues.channel) {
      params.channel = formValues.channel;
    }
    if (formValues.payType) {
      params.payType = formValues.payType;
    }
    if (formValues.status) {
      params.status = formValues.status;
    }
    if (formValues.notifyStatus) {
      params.notifyStatus = formValues.notifyStatus;
    }
    if (formValues.createdAt?.[0] && formValues.createdAt?.[1]) {
      params.startDate = formValues.createdAt[0].format('YYYY-MM-DD');
      params.endDate = formValues.createdAt[1].format('YYYY-MM-DD');
    }

    return params;
  }, [searchForm]);

  const loadOrders = useCallback(async (page = 1, pageSize = pagination.pageSize, values?: SearchFormValues) => {
    setLoading(true);
    try {
      const response = await paymentOrderService.list(buildQueryParams(page, pageSize, values));
      setOrders(response.list || []);
      setStats(response.stats || defaultStats);
      setPagination({
        page: response.page || page,
        pageSize: response.pageSize || pageSize,
        total: response.total || 0,
      });
    } catch {
      message.error('获取支付记录失败');
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, pagination.pageSize]);

  const loadNotifyRows = useCallback(async (orderNo: string) => {
    if (!orderNo) {
      setNotifyRows([]);
      return;
    }
    setNotifyLoading(true);
    try {
      const response = await paymentNotifyService.list({
        page: 1,
        pageSize: 50,
        orderNo,
      });
      setNotifyRows(response.list || []);
    } catch {
      message.error('获取回调记录失败');
    } finally {
      setNotifyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders(1, pagination.pageSize);
  }, [loadOrders, pagination.pageSize]);

  const handleSearch = async () => {
    const values = searchForm.getFieldsValue();
    await loadOrders(1, pagination.pageSize, values);
  };

  const handleReset = async () => {
    searchForm.resetFields();
    await loadOrders(1, pagination.pageSize, {});
  };

  const handleOpenDetail = async (record: PaymentOrder) => {
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const [orderDetail] = await Promise.all([
        paymentOrderService.get(record.id),
        loadNotifyRows(record.orderNo),
      ]);
      setCurrentOrder(orderDetail);
    } catch {
      message.error('获取支付详情失败');
      setDetailVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenNotifyDetail = async (record: PaymentNotify) => {
    try {
      const detail = await paymentNotifyService.get(record.id);
      setCurrentNotify(detail);
      setNotifyDetailVisible(true);
    } catch {
      message.error('获取回调详情失败');
    }
  };

  const handleRetryNotify = async (record: PaymentNotify) => {
    try {
      await paymentNotifyService.retry(record.id);
      message.success('已触发回调重试');
      if (currentOrder?.orderNo) {
        await loadNotifyRows(currentOrder.orderNo);
        await loadOrders(pagination.page, pagination.pageSize);
      }
    } catch {
      message.error('回调重试失败');
    }
  };

  const orderColumns: ColumnsType<PaymentOrder> = [
    {
      title: '支付单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 190,
      fixed: 'left',
      render: (value: string) => <Text copyable>{value}</Text>,
    },
    {
      title: '业务订单号',
      dataIndex: 'bizOrderNo',
      key: 'bizOrderNo',
      width: 190,
      render: (value?: string) => value ? <Text copyable>{value}</Text> : '-',
    },
    {
      title: '业务类型',
      dataIndex: 'bizType',
      key: 'bizType',
      width: 120,
      render: (value?: string) => renderTag(value, bizTypeMap),
    },
    {
      title: '支付标题',
      dataIndex: 'subject',
      key: 'subject',
      width: 220,
      ellipsis: true,
      render: (value?: string) => value || '-',
    },
    {
      title: '付款用户',
      dataIndex: 'userName',
      key: 'userName',
      width: 120,
      render: (value?: string) => value || '-',
    },
    {
      title: '支付渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 110,
      render: (value?: string) => renderTag(value, channelMap),
    },
    {
      title: '支付方式',
      dataIndex: 'payType',
      key: 'payType',
      width: 110,
      render: (value?: string) => renderTag(value, payTypeMap),
    },
    {
      title: '支付场景',
      dataIndex: 'payScene',
      key: 'payScene',
      width: 110,
      render: (value?: string) => renderTag(value, paySceneMap),
    },
    {
      title: '支付金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (value: number, record) => `${formatMoney(value)} ${record.currency || 'CNY'}`,
    },
    {
      title: '支付状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value?: string) => renderTag(value, orderStatusMap),
    },
    {
      title: '回调状态',
      dataIndex: 'notifyStatus',
      key: 'notifyStatus',
      width: 120,
      render: (value?: string) => renderTag(value, notifyStatusMap),
    },
    {
      title: '微信交易号',
      dataIndex: 'tradeNo',
      key: 'tradeNo',
      width: 220,
      render: (value?: string) => value ? <Text copyable>{value}</Text> : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: '支付时间',
      dataIndex: 'paidAt',
      key: 'paidAt',
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleOpenDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  const notifyColumns: ColumnsType<PaymentNotify> = [
    {
      title: '通知单号',
      dataIndex: 'notifyNo',
      key: 'notifyNo',
      width: 190,
      render: (value: string) => <Text copyable>{value}</Text>,
    },
    {
      title: '通知类型',
      dataIndex: 'notifyType',
      key: 'notifyType',
      width: 140,
      render: (value?: string) => renderTag(value, notifyTypeMap),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value?: string) => renderTag(value, notifyStatusMap),
    },
    {
      title: '重试次数',
      key: 'retryCount',
      width: 100,
      render: (_, record) => `${record.retryCount}/${record.maxRetry}`,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: '成功时间',
      dataIndex: 'successAt',
      key: 'successAt',
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleOpenNotifyDetail(record)}>
            查看
          </Button>
          {record.status === 'failed' ? (
            <Button type="link" size="small" onClick={() => handleRetryNotify(record)}>
              重试
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Card title={<Space><WalletOutlined />订单支付记录</Space>}>
        <Form form={searchForm} layout="inline" onFinish={handleSearch}>
          <Form.Item name="orderNo">
            <Input placeholder="支付单号/业务订单号" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="subject">
            <Input placeholder="支付标题" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="userName">
            <Input placeholder="付款用户" allowClear style={{ width: 140 }} />
          </Form.Item>
          <Form.Item name="tradeNo">
            <Input placeholder="微信交易号" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="channel">
            <Select
              placeholder="支付渠道"
              allowClear
              style={{ width: 120 }}
              options={[
                { label: '微信支付', value: 'wechat' },
                { label: '支付宝', value: 'alipay' },
                { label: '钱包支付', value: 'wallet' },
              ]}
            />
          </Form.Item>
          <Form.Item name="payType">
            <Select
              placeholder="支付方式"
              allowClear
              style={{ width: 120 }}
              options={[
                { label: 'JSAPI', value: 'jsapi' },
                { label: 'Native', value: 'native' },
                { label: 'App', value: 'app' },
                { label: 'WAP', value: 'wap' },
              ]}
            />
          </Form.Item>
          <Form.Item name="status">
            <Select
              placeholder="支付状态"
              allowClear
              style={{ width: 120 }}
              options={[
                { label: '待支付', value: 'pending' },
                { label: '已支付', value: 'paid' },
                { label: '支付失败', value: 'failed' },
                { label: '已关闭', value: 'closed' },
              ]}
            />
          </Form.Item>
          <Form.Item name="notifyStatus">
            <Select
              placeholder="回调状态"
              allowClear
              style={{ width: 120 }}
              options={[
                { label: '待回调', value: 'pending' },
                { label: '回调成功', value: 'success' },
                { label: '回调失败', value: 'failed' },
                { label: '已忽略', value: 'ignored' },
              ]}
            />
          </Form.Item>
          <Form.Item name="createdAt">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Row gutter={16}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="支付订单数" value={stats.totalCount} suffix="单" />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="订单总金额" value={stats.totalAmount} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="已支付金额" value={stats.paidAmount} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="回调异常数" value={stats.notifyFailedCount} suffix="单" />
          </Card>
        </Col>
      </Row>

      <Card>
        <div ref={tableWrapRef}>
        <Table
          rowKey="id"
          loading={loading}
          columns={orderColumns}
          dataSource={orders}
          scroll={{ x: 2100 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => loadOrders(page, pageSize),
          }}
        />
        </div>
      </Card>

      <Modal
        title="支付记录详情"
        open={detailVisible}
        onCancel={() => {
          setDetailVisible(false);
          setCurrentOrder(null);
          setNotifyRows([]);
        }}
        footer={null}
        width={1180}
        destroyOnHidden
      >
        {currentOrder ? (
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <Card loading={detailLoading} size="small" title="支付信息">
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="支付单号">{currentOrder.orderNo}</Descriptions.Item>
                <Descriptions.Item label="业务订单号">{currentOrder.bizOrderNo || '-'}</Descriptions.Item>
                <Descriptions.Item label="业务类型">{renderTag(currentOrder.bizType, bizTypeMap)}</Descriptions.Item>
                <Descriptions.Item label="应用标识">{currentOrder.appCode || '-'}</Descriptions.Item>
                <Descriptions.Item label="支付标题">{currentOrder.subject || '-'}</Descriptions.Item>
                <Descriptions.Item label="付款用户">{currentOrder.userName || '-'}</Descriptions.Item>
                <Descriptions.Item label="支付渠道">{renderTag(currentOrder.channel, channelMap)}</Descriptions.Item>
                <Descriptions.Item label="支付方式">{renderTag(currentOrder.payType, payTypeMap)}</Descriptions.Item>
                <Descriptions.Item label="支付场景">{renderTag(currentOrder.payScene, paySceneMap)}</Descriptions.Item>
                <Descriptions.Item label="支付状态">{renderTag(currentOrder.status, orderStatusMap)}</Descriptions.Item>
                <Descriptions.Item label="回调状态">{renderTag(currentOrder.notifyStatus, notifyStatusMap)}</Descriptions.Item>
                <Descriptions.Item label="回调次数">{currentOrder.notifyCount || 0}</Descriptions.Item>
                <Descriptions.Item label="支付金额">{formatMoney(currentOrder.amount)} {currentOrder.currency || 'CNY'}</Descriptions.Item>
                <Descriptions.Item label="微信交易号">{currentOrder.tradeNo || '-'}</Descriptions.Item>
                <Descriptions.Item label="付款人 OpenID">{currentOrder.payerOpenId || '-'}</Descriptions.Item>
                <Descriptions.Item label="用户 ID">{currentOrder.userId || '-'}</Descriptions.Item>
                <Descriptions.Item label="创建时间">{formatDateTime(currentOrder.createdAt)}</Descriptions.Item>
                <Descriptions.Item label="支付时间">{formatDateTime(currentOrder.paidAt)}</Descriptions.Item>
                <Descriptions.Item label="最近回调时间">{formatDateTime(currentOrder.notifyTime)}</Descriptions.Item>
                <Descriptions.Item label="关闭时间">{formatDateTime(currentOrder.closedAt)}</Descriptions.Item>
                <Descriptions.Item label="过期时间" span={2}>{formatDateTime(currentOrder.expireTime)}</Descriptions.Item>
                <Descriptions.Item label="回调地址" span={2}>{currentOrder.notifyUrl || '-'}</Descriptions.Item>
                <Descriptions.Item label="返回地址" span={2}>{currentOrder.returnUrl || '-'}</Descriptions.Item>
                <Descriptions.Item label="订单描述" span={2}>{currentOrder.body || '-'}</Descriptions.Item>
                <Descriptions.Item label="备注" span={2}>{currentOrder.remark || '-'}</Descriptions.Item>
                <Descriptions.Item label="扩展数据" span={2}>
                  <pre style={{ margin: 0, maxHeight: 180, overflow: 'auto' }}>
                    {formatPayload(currentOrder.extraData)}
                  </pre>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="回调记录">
              <Table
                rowKey="id"
                size="small"
                loading={notifyLoading}
                columns={notifyColumns}
                dataSource={notifyRows}
                pagination={false}
                scroll={{ x: 1100 }}
              />
            </Card>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title="回调详情"
        open={notifyDetailVisible}
        onCancel={() => {
          setNotifyDetailVisible(false);
          setCurrentNotify(null);
        }}
        footer={null}
        width={920}
        destroyOnHidden
      >
        {currentNotify ? (
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="通知单号">{currentNotify.notifyNo}</Descriptions.Item>
            <Descriptions.Item label="订单号">{currentNotify.orderNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="应用标识">{currentNotify.appCode || '-'}</Descriptions.Item>
            <Descriptions.Item label="通知类型">{renderTag(currentNotify.notifyType, notifyTypeMap)}</Descriptions.Item>
            <Descriptions.Item label="支付渠道">{renderTag(currentNotify.channel, channelMap)}</Descriptions.Item>
            <Descriptions.Item label="回调状态">{renderTag(currentNotify.status, notifyStatusMap)}</Descriptions.Item>
            <Descriptions.Item label="重试次数">{currentNotify.retryCount}/{currentNotify.maxRetry}</Descriptions.Item>
            <Descriptions.Item label="下次重试时间">{formatDateTime(currentNotify.nextRetryAt)}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{formatDateTime(currentNotify.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="成功时间">{formatDateTime(currentNotify.successAt)}</Descriptions.Item>
            <Descriptions.Item label="回调地址" span={2}>{currentNotify.notifyUrl || '-'}</Descriptions.Item>
            <Descriptions.Item label="错误信息" span={2}>{currentNotify.errorMsg || '-'}</Descriptions.Item>
            <Descriptions.Item label="请求数据" span={2}>
              <pre style={{ margin: 0, maxHeight: 220, overflow: 'auto' }}>
                {formatPayload(currentNotify.requestData)}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label="响应数据" span={2}>
              <pre style={{ margin: 0, maxHeight: 220, overflow: 'auto' }}>
                {formatPayload(currentNotify.responseData)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Modal>
    </Space>
  );
};

export default PaymentNotifyPage;
